import { MutationCache, QueryCache } from 'react-query';
import { AxiosError } from 'axios';
import { snackNotifications } from './SnackBarUtils';
import { startCase } from 'lodash';
import { hasBearerToken } from '@dispatch/shared';
import { ErrorResponse } from '@dispatch/shared/types';

type MutationType = 'create' | 'save' | 'deactivate' | 'upsert';
const verbSet: Record<MutationType, string> = {
  create: 'Created',
  deactivate: 'Deactivated',
  save: 'Saved',
  upsert: 'Updated',
};

export const ClientCache = {
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: unknown) => {
        // Extract status from wrapped ErrorResponse or raw AxiosError
        const status =
          (error as ErrorResponse)?.error?.response?.status ??
          (error as AxiosError)?.response?.status;

        // Don't retry client errors (4xx) -- these won't succeed on retry
        if (status && status >= 400 && status < 500) return false;

        // Retry server/network errors up to 3 times
        return failureCount < 3;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (e, _variables, _context, mutation) => {
      const mutationKey = (
        mutation?.options?.mutationKey?.[0] as string
      )?.split('-');
      const type: string = mutationKey?.[0];
      const entity: string = mutationKey?.[1];
      const verb: string = verbSet[type as MutationType];
      const error = e as Error;

      if (entity && verb) {
        snackNotifications.error(
          `${startCase(entity)} was not ${verb.toLowerCase()}`,
          error?.message
        );
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (e) => {
      const error = e as ErrorResponse;
      const errorConfig = error?.error?.config;

      if (hasBearerToken(errorConfig)) {
        snackNotifications.error('Error', error?.message, {
          preventDuplicate: true,
        });
      }
    },
  }),
};
